import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ABORT_ERROR, makeApiRequests } from '../../../../../helpers/api';
import { ENDPOINTS } from '../../../../../helpers/constants';
import useLocalization from '../../../../../hooks/useLocalization';
import CircularProgressBar from '../../../../common/circular-progress';
import Logs from '../../../../common/logs/Logs';

const initialPageInfo = {
  pageSize: 20,
  pageNumber: 1
};

const UserLogsModal = ({ show, user, onHide, disabled, onUndoRedoClick }) => {
  const [fetchingFirstPageLogs, setFetchingFirstPageLogs] = useState(true);
  const [logs, setLogs] = useState([]);
  const { translate } = useLocalization();
  const [fetchingMoreLogs, setFetchingMoreLogs] = useState(false);
  const [toLoadPageInfo, setToLoadPageInfo] = useState(initialPageInfo);
  const [loadedPageInfo, setLoadedPageInfo] = useState();
  const abortControllerRef = useRef(null);

  const getLogs = async () => {
    if (!show || !user?._id || !toLoadPageInfo) {
      setLogs([]);
      setFetchingFirstPageLogs(false);
      return;
    }

    // If an old API call is in progress, abort it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const pageToFetch = toLoadPageInfo?.pageNumber;
    const loadingMoreLogs = pageToFetch > 1;

    if (loadingMoreLogs) {
      setFetchingMoreLogs(true);
    } else {
      setFetchingFirstPageLogs(true);
    }

    const controller = new AbortController();
    const { signal } = controller;

    abortControllerRef.current = controller;

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_LOGS(user._id),
      requestBody: {
        ...toLoadPageInfo,
        pageSize: initialPageInfo.pageSize
      },
      signal
    });

    if (error === ABORT_ERROR) return;

    setFetchingFirstPageLogs(false);
    setFetchingMoreLogs(false);
    if (error) {
      toast.error(error);
      return;
    }

    const { pageNumber, totalPages, results = [] } = response;
    setLoadedPageInfo({ totalPages, pageNumber });
    setLogs(loadingMoreLogs ? [...logs, ...results] : results);
  };

  const getMoreLogs = async () => {
    if (!loadedPageInfo || fetchingFirstPageLogs || fetchingMoreLogs) return;

    if (loadedPageInfo.pageNumber >= loadedPageInfo.totalPages) return;

    setToLoadPageInfo({
      ...loadedPageInfo,
      pageNumber: loadedPageInfo.pageNumber + 1
    });
  };

  useEffect(() => {
    getLogs();
  }, [toLoadPageInfo]);

  useEffect(() => {
    setLogs([]);

    if (show && user?._id) {
      setToLoadPageInfo({ ...initialPageInfo });
    }
  }, [show, user]);

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onHide}
      centered
      dialogClassName="h-100 my-0 py-5 px-5"
      contentClassName="d-flex flex-column h-100 justify-self-center"
    >
      <Modal.Header closeButton>
        <Modal.Title className="flex-grow-1">
          <h5 className="mb-0">{translate('logs')}</h5>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto h-100 py-1 px-4">
        {fetchingFirstPageLogs ? (
          <div className="p-5 text-center">
            <CircularProgressBar size={5} />
          </div>
        ) : (
          <Logs
            user={user}
            logs={logs}
            disabled={disabled}
            onUndoRedoClick={onUndoRedoClick}
            onBottomReached={getMoreLogs}
            loadingMoreLogs={fetchingMoreLogs}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UserLogsModal;
